import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const API_URL = '';

// Default gallery images
const DEFAULT_GALLERY = [
  { url: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80', title: 'Vue aérienne', category: 'aerial' },
  { url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80', title: 'Chantier', category: 'construction' },
  { url: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&q=80', title: 'Golf', category: 'golf' },
  { url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80', title: 'Villa', category: 'residential' },
  { url: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80', title: 'Nature', category: 'nature' },
  { url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80', title: 'Architecture', category: 'architecture' },
  { url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80', title: 'Intérieur', category: 'interior' },
  { url: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80', title: 'Lifestyle', category: 'lifestyle' },
];

// Category labels
const CATEGORY_LABELS = {
  aerial: { fr: 'Vue aérienne', en: 'Aerial' },
  construction: { fr: 'Chantier', en: 'Construction' },
  golf: { fr: 'Golf', en: 'Golf' },
  residential: { fr: 'Résidentiel', en: 'Residential' },
  nature: { fr: 'Nature', en: 'Nature' },
  architecture: { fr: 'Architecture', en: 'Architecture' },
  interior: { fr: 'Intérieur', en: 'Interior' },
  lifestyle: { fr: 'Lifestyle', en: 'Lifestyle' },
  other: { fr: 'Autre', en: 'Other' }
};

const GalleryImmersive = () => {
  const { language } = useLanguage();
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);

  // Fetch gallery images from CMS
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/gallery?limit=20`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setGalleryImages(data.map(img => ({
              url: img.url,
              title: img.title,
              category: CATEGORY_LABELS[img.category]?.[language] || img.category,
            })));
          } else {
            setGalleryImages(DEFAULT_GALLERY.map(img => ({
              url: img.url,
              title: img.title,
              category: CATEGORY_LABELS[img.category]?.[language] || img.category
            })));
          }
        } else {
          setGalleryImages(DEFAULT_GALLERY.map(img => ({
            url: img.url,
            title: img.title,
            category: CATEGORY_LABELS[img.category]?.[language] || img.category
          })));
        }
      } catch (error) {
        setGalleryImages(DEFAULT_GALLERY.map(img => ({
          url: img.url,
          title: img.title,
          category: CATEGORY_LABELS[img.category]?.[language] || img.category
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [language]);

  // Intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Triple the images for seamless infinite loop
  const tripleImages = [...galleryImages, ...galleryImages, ...galleryImages];

  if (loading) {
    return (
      <section id="galerie" ref={sectionRef} className="relative py-28 lg:py-40 overflow-hidden" style={{ backgroundColor: '#faf9f6' }}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  return (
    <section 
      id="galerie" 
      ref={sectionRef}
      className="relative py-28 lg:py-40 overflow-hidden"
      style={{ backgroundColor: '#faf9f6' }}
    >
      {/* Background Subtle Gradient */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(250,249,246,1), rgba(245,240,230,0.5), rgba(250,249,246,1))' }} />

      {/* Section Header */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 mb-12 lg:mb-16">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <span className="text-xs font-sans font-medium tracking-[0.3em] uppercase" style={{ color: '#c4a76c' }}>
            {language === 'fr' ? 'Galerie' : 'Gallery'}
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl" style={{ color: '#1e4a1a' }}>
            {language === 'fr' ? 'L\'Excellence en' : 'Excellence in'}{' '}
            <span style={{ color: '#c4a76c' }}>{language === 'fr' ? 'Action' : 'Action'}</span>
          </h2>
        </motion.div>
      </div>

      {/* Single Line Infinite Marquee */}
      <motion.div 
        className="relative z-10"
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.3 }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Fade edges for seamless look */}
        <div className="absolute left-0 top-0 bottom-0 w-32 z-20 pointer-events-none" style={{ background: 'linear-gradient(to right, #faf9f6, transparent)' }} />
        <div className="absolute right-0 top-0 bottom-0 w-32 z-20 pointer-events-none" style={{ background: 'linear-gradient(to left, #faf9f6, transparent)' }} />

        {/* Marquee Track */}
        <div 
          className="flex gap-6"
          style={{
            animation: `marquee-scroll ${galleryImages.length * 4}s linear infinite`,
            animationPlayState: isPaused ? 'paused' : 'running',
          }}
        >
          {tripleImages.map((image, index) => (
            <div
              key={`${image.url}-${index}`}
              className="flex-shrink-0 group relative"
            >
              {/* Image Container - Fixed Height */}
              <div 
                className="relative overflow-hidden rounded-lg bg-gray-100"
                style={{ width: '320px', height: '220px' }}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800';
                  }}
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Category Badge */}
                <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-sans font-medium text-stone-800">
                    {image.category}
                  </span>
                </div>
                
                {/* Title on Hover */}
                <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
                  <p className="text-white font-sans text-sm font-medium">
                    {image.title}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Bottom Hint */}
      <motion.div 
        className="relative z-10 text-center mt-10"
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <p className="text-gray-400 font-sans text-xs tracking-wider">
          {language === 'fr' ? '↔ Survolez pour explorer ↔' : '↔ Hover to explore ↔'}
        </p>
      </motion.div>

      {/* CSS Animation */}
      <style>{`
        @keyframes marquee-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
      `}</style>
    </section>
  );
};

export default GalleryImmersive;
